import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { CreateVisitBookingAdminSchema } from "@sylva/shared-types/schemas";

type CreateVisitParams = Omit<z.infer<typeof CreateVisitBookingAdminSchema>, 'tenant_id'>;

// Zod schemas for visit operations
const UpdateVisitParamsSchema = z.object({
  visit_id: z.string().uuid(),
  appointment_type_ids: z.array(z.string().uuid()).optional(),
  team_member_ids: z.array(z.string().uuid()).optional(),
  start_time: z.string().datetime().optional(),
  notes: z.string().nullable().optional(),
  status: z.enum(["confirmed", "pending", "cancelled", "no_show"]).optional(),
  notify_customer: z.boolean().optional(),
});

const CancelVisitParamsSchema = z.object({
  visit_id: z.string().uuid(),
  notify_customer: z.boolean().optional(),
  cancellation_reason: z.string().optional(),
});

type UpdateVisitParams = z.infer<typeof UpdateVisitParamsSchema>;
type CancelVisitParams = z.infer<typeof CancelVisitParamsSchema>;

export const useCreateVisit = () => {
  const { currentTenant } = useTenant();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateVisitParams) => {
      if (!currentTenant?.id) throw new Error('No tenant selected');

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) throw new Error('Authentication required');

      const body: Record<string, unknown> = {
        tenant_id: currentTenant.id,
        customer_id: params.customer_id,
        appointment_type_ids: params.appointment_type_ids,
        start_time: params.start_time,
        notes: params.notes ?? null,
      };

      if (params.team_member_ids && params.team_member_ids.length > 0) {
        body.team_member_ids = params.team_member_ids;
      } else if (params.team_member_id) {
        body.team_member_id = params.team_member_id;
      }

      if (params.notify_customer !== undefined) {
        body.notify_customer = params.notify_customer;
      }

      const { data, error } = await supabase.functions.invoke('create-visit', {
        body,
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message || 'Failed to create appointment bundle');
      if (!data?.success) throw new Error(data?.error || 'Failed to create appointment bundle');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-appointments'] });
      toast.success(t('appointmentCreated'));
    },
    onError: (err: any) => {
      toast.error(err?.message || t('failedToCreateAppointment'));
    }
  });
};

export const useUpdateVisit = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateVisitParams) => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) throw new Error('Authentication required');

      const { data, error } = await supabase.functions.invoke('update-visit', {
        body: params,
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message || 'Failed to update visit');
      if (!data?.success) throw new Error(data?.error || 'Failed to update visit');
      return data;
    },
    onSuccess: () => {
      toast.success(t('visitUpdated'));
      // Trust the optimistic update - no refetch to avoid jump during render
      // If mutation succeeds, optimistic update is already correct
    },
    onError: (err: any) => {
      toast.error(err?.message || t('failedToUpdateVisit'));
      // Invalidate queries to revert to server state
      queryClient.invalidateQueries({ queryKey: ['calendar-appointments'] });
    }
  });
};

export const useCancelVisit = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CancelVisitParams) => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) throw new Error('Authentication required');

      const { data, error } = await supabase.functions.invoke('cancel-visit', {
        body: params,
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message || 'Failed to cancel visit');
      if (!data?.success) throw new Error(data?.error || 'Failed to cancel visit');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-appointments'] });
      toast.success(t('visitCancelled'));
    },
    onError: (err: any) => {
      toast.error(err?.message || t('failedToCancelVisit'));
    }
  });
};
